"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _ViewportProvider = require("../ViewportProvider");
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _StackView = _interopRequireWildcard(require("../StackView"));
var _RadioCard = _interopRequireDefault(require("./RadioCard"));
var _Fieldset = _interopRequireDefault(require("../Fieldset"));
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);
const [selectItemProps, selectedItemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.focusHandlerProps, _utils.viewProps]);

/**
 * A group of Cards that behave as a radio button group. Use when users select a single choice from mutually
 * exclusive options with need to show additional information for each option. The whole cards are each
 * interactive as one item.
 *
 * ## Component API
 *
 * Use the `items` prop to pass an array of objects describing each RadioCard in the group:
 *
 *  - `title`: main text passed to RadioCard's `title` prop
 *  - `content`: React content passed to RadioCard's `children` prop
 *  - `id`: identifier used to store which RadioCard is selected (uses `title` if undefinded)
 *  - `onChange`: optional function called on selection, in addition to updating the group's selection state
 *
 * ### Controlled version
 *
 * If the selection state is controlled from outside, it needs to receive `checkedId` and `onChange` props.
 *
 * ### Uncontrolled version
 *
 * If the RadioCardGroup manages its own state, you can use `initialCheckedId` prop to provide the initial value.
 * Whenever the radio card gets toggled, it calls the `onChange` callback with the new value (string).
 *
 * ### Use in forms
 *
 * For web forms, the `name` prop may be used to define the name of the group's `fieldset` and input elements.
 *
 * ### Validation
 *
 * Validation state may be set by passing 'error' or 'success' to the `validation` prop.
 *
 * ## A11y guidelines
 *
 * RadioCardGroup accepts all the common accessibility props, but also sets accessibility role `'radiogroup'`
 * and controls the accessibility state of children like RadioCard and Feedback based on current state.
 *
 * @example
 * ```jsx
 * <RadioCardGroup
 *   initialCheckedId="card1"
 *   items={[
 *     { title: 'Card 1', id: 'card1', content: <Typography>More info</Typography> },
 *     { title: 'Card 2', id: 'card2', content: <Typography>Extra content</Typography> },
 *     { title: 'Card 3', id: 'card3', content: <Typography>More content</Typography> }
 *   ]}
 *   legend="Cards"
 *   hint="Choose a card to select"
 * />
 * ```
 */
const RadioCardGroup = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    copy = 'en',
    tokens,
    radioCardTokens,
    variant,
    items = [],
    legend,
    tooltip,
    hint,
    hintPosition = 'inline',
    validation,
    feedback,
    initialCheckedId,
    checkedId,
    onChange,
    readOnly,
    name: inputGroupName,
    inactive,
    ...rest
  } = _ref;
  const viewport = (0, _ViewportProvider.useViewport)();
  const {
    space,
    fieldSpace,
    direction,
    showIcon,
    outlineWidth,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    outlineOffset
  } = (0, _ThemeProvider.useThemeTokens)('RadioCardGroup', tokens, variant, {
    viewport
  });
  const borderTokens = {
    outlineWidth,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    outlineOffset
  };
  const {
    currentValue,
    setValue
  } = (0, _utils.useInputValue)({
    value: checkedId,
    initialValue: initialCheckedId,
    onChange,
    readOnly: readOnly || inactive
  });
  const StackContainer = direction === 'row' ? _StackView.StackWrap : _StackView.default;
  // Needs 'radiogroup' role on direct parent of radios for MacOS Voiceover's numbering to work,
  // and also needs 'radiogroup' role on fieldset for correct description on focusing the set.
  // TODO: test this on more web screen readers.

  const uniqueFields = ['id'];
  if (!(0, _utils.containUniqueFields)(items, uniqueFields)) {
    throw new Error(`RadioCardGroup items must have unique ${uniqueFields.join(', ')}`);
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Fieldset.default, {
    copy: copy,
    ref: ref,
    name: inputGroupName,
    legend: legend,
    tooltip: tooltip,
    hint: hint,
    hintPosition: hintPosition,
    space: fieldSpace,
    feedback: feedback,
    inactive: inactive || readOnly,
    validation: validation,
    showErrorBorder: true,
    tokens: borderTokens,
    showIcon: showIcon,
    accessibilityRole: "radiogroup",
    ...selectProps(rest),
    children: props => /*#__PURE__*/(0, _jsxRuntime.jsx)(StackContainer, {
      space: space,
      accessibilityRole: "radiogroup",
      children: items.map((_ref2, index) => {
        let {
          title,
          content,
          id,
          onChange: itemOnChange,
          ...itemRest
        } = _ref2;
        const cardId = id || `RadioCard[${index}]`;
        const handleChange = (newCheckedState, event) => {
          if (typeof itemOnChange === 'function') itemOnChange(newCheckedState, event);
          if (newCheckedState) setValue(cardId, event);
        };
        return /*#__PURE__*/(0, _jsxRuntime.jsx)(_RadioCard.default, {
          id: cardId,
          checked: currentValue === cardId,
          onChange: handleChange,
          inactive: inactive,
          title: title,
          name: inputGroupName,
          tokens: radioCardTokens,
          variant: variant,
          readOnly: readOnly,
          ...selectItemProps(itemRest),
          ...props,
          children: content
        }, cardId);
      })
    })
  });
});
RadioCardGroup.displayName = 'RadioCardGroup';
RadioCardGroup.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Whether the English or French copy will be used (e.g. for accessibility labels).
   */
  copy: _propTypes.default.oneOf(['en', 'fr']),
  /**
   * Optional theme token overrides for the outer RadioCardGroup component
   */
  tokens: (0, _utils.getTokensPropType)('RadioCardGroup'),
  /**
   * Optional theme token overrides for each inner RadioCard component
   */
  radioCardTokens: (0, _utils.getTokensPropType)('RadioCard'),
  /**
   * Theme variants, shared between both RadioCardGroup and RadioCard
   */
  variant: _utils.variantProp.propType,
  /**
   * Array of objects containing specifics for each RadioCard to be rendered in the group.
   */
  items: _propTypes.default.arrayOf(_propTypes.default.exact({
    ...selectedItemPropTypes,
    title: _propTypes.default.string,
    content: _propTypes.default.node,
    id: _propTypes.default.string,
    onChange: _propTypes.default.func
  })),
  /**
   * Main text used to describe this group, used in Fieldset's Legend element.
   */
  legend: _propTypes.default.string,
  /**
   * Optional additional text giving more detail to help a user make a choice.
   */
  hint: _propTypes.default.string,
  /**
   * Position of the hint relative to label. Use `below` to display a larger hint below the label.
   */
  hintPosition: _propTypes.default.oneOf(['inline', 'below']),
  /**
   * Optional tooltip text content to include alongside the legend and hint.
   */
  tooltip: _propTypes.default.string,
  /**
   * Current validation status of the group, passed to the feedback element if there is one.
   */
  validation: _propTypes.default.oneOf(['error', 'success']),
  /**
   * If provided, a Feedback element is rendered containing this text.
   */
  feedback: _propTypes.default.string,
  /**
   * If provided, the radio card with this id is selected on first render.
   */
  initialCheckedId: _propTypes.default.string,
  /**
   * If not undefined, the radio card with this id is selected (or none is selected if `null`), and the
   * element's selection state will be controlled by its parent using the `onChange` function.
   */
  checkedId: _propTypes.default.string,
  /**
   * Function to call on change in selection state. Is required if the selection state is controlled
   * by a parent using checkedId and the input is not readOnly.
   */
  onChange: _propTypes.default.func,
  /**
   * If true, the radio cards cannot be selected by the user and simply show their current state.
   */
  readOnly: _propTypes.default.bool,
  /**
   * If true, the radio card cannot be interacted with, elements are set as `disabled` and if the
   * theme supports `inactive` appearances rules, these are applied.
   */
  inactive: _propTypes.default.bool,
  /**
   * On Web, this is passed to the `name` attribute of the fieldset and each radio input.
   */
  name: _propTypes.default.string
};
var _default = exports.default = RadioCardGroup;